import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, output, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { DialogFocusDirective } from './dialog-focus.directive';
import { tagName } from './documents-query';
import { DocTag, DocTagRename } from './documents.models';
import { DocumentsService } from './documents.service';
import { viewRefresher } from './view-refresh';

/**
 * Administrators: rename a tag on every document the role may change, or merge it into another one (a document having
 * both keeps both values, joined). The number of documents is checked first, then the change is confirmed.
 */
@Component({
  selector: 'iu-document-tag-manager',
  standalone: true,
  imports: [TranslateModule, DialogFocusDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="tm-backdrop" (click)="closed.emit()"></div>
    <section class="tm" role="dialog" aria-modal="true" aria-labelledby="tm-title" iuDialogFocus (keydown.escape)="closed.emit()">
      <header class="tm-head">
        <h2 id="tm-title">{{ 'documents.tagManager.title' | translate }}</h2>
        <button type="button" class="tm-icon" (click)="closed.emit()" [attr.aria-label]="'documents.actions.close' | translate"><i class="fa-solid fa-xmark" aria-hidden="true"></i></button>
      </header>
      <form class="tm-body" (submit)="$event.preventDefault(); check()">
        <p class="tm-muted">{{ 'documents.tagManager.help' | translate }}</p>
        <div class="tm-row">
          <label class="tm-field">
            <span>{{ 'documents.tagManager.from' | translate }}</span>
            <input class="p-inputtext tm-input" list="tm-tag-names" maxlength="60" data-autofocus [value]="from()" (input)="setFrom($any($event.target).value)" />
          </label>
          <i class="fa-solid fa-arrow-right tm-arrow" aria-hidden="true"></i>
          <label class="tm-field">
            <span>{{ 'documents.tagManager.to' | translate }}</span>
            <input class="p-inputtext tm-input" list="tm-tag-names" maxlength="60" [value]="to()" (input)="setTo($any($event.target).value)" />
          </label>
        </div>
        <datalist id="tm-tag-names">
          @for (t of names(); track t.key) {
            <option [value]="t.key">{{ t.label }}</option>
          }
        </datalist>
        @if (error()) {
          <p class="tm-error" role="alert"><i class="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>{{ error() }}</p>
        }
        @if (checked(); as c) {
          <div class="tm-result" aria-live="polite">
            @if (c.matched === 0) {
              <p>{{ 'documents.tagManager.none' | translate: { from: c.from } }}</p>
            } @else {
              <p>{{ 'documents.tagManager.count' | translate: { count: c.matched, from: c.from, to: c.to } }}</p>
              @if (c.merged > 0) {
                <p class="tm-warn"><i class="fa-solid fa-code-merge" aria-hidden="true"></i>{{ 'documents.tagManager.merged' | translate: { count: c.merged, to: c.to } }}</p>
              }
              @if (existing()) {
                <p class="tm-muted">{{ 'documents.tagManager.mergeNote' | translate: { to: c.to } }}</p>
              }
            }
          </div>
        }
        <button type="submit" hidden></button>
      </form>
      <footer class="tm-foot">
        <span class="tm-spacer"></span>
        <button type="button" class="p-button p-button-secondary tm-btn" (click)="closed.emit()">{{ 'documents.actions.cancel' | translate }}</button>
        @if (!checked() || checked()!.matched === 0) {
          <button type="button" class="p-button p-button-primary tm-btn" [disabled]="!valid() || busy()" (click)="check()">
            @if (busy()) {<i class="fa-solid fa-circle-notch fa-spin" aria-hidden="true"></i>}{{ 'documents.tagManager.check' | translate }}
          </button>
        } @else {
          <button type="button" class="p-button p-button-primary tm-btn" [disabled]="busy()" (click)="apply()">
            @if (busy()) {<i class="fa-solid fa-circle-notch fa-spin" aria-hidden="true"></i>}{{ (existing() ? 'documents.tagManager.merge' : 'documents.tagManager.rename') | translate: { count: checked()!.matched } }}
          </button>
        }
      </footer>
    </section>
  `,
  styles: `
    :host { position: absolute; inset: 0; z-index: 20; display: flex; align-items: center; justify-content: center; }
    .tm-backdrop { position: absolute; inset: 0; background: #0006; }
    .tm { position: relative; display: flex; flex-direction: column; width: min(560px, calc(100% - 32px)); max-height: calc(100% - 32px);
      border: 1px solid var(--fieldset-bd-color); border-radius: var(--global-border-radius); background: var(--fieldset-bg-color);
      color: var(--global-text-color-1); box-shadow: var(--global-shadow-box); }
    .tm-head, .tm-foot { display: flex; align-items: center; gap: 8px; padding: 10px 14px; }
    .tm-head { border-bottom: 1px solid var(--docs-bd-color); }
    .tm-head h2 { flex: 1 1 auto; margin: 0; font-size: 15px; }
    .tm-foot { border-top: 1px solid var(--docs-bd-color); flex-wrap: wrap; }
    .tm-body { flex: 1 1 auto; min-height: 0; overflow: auto; padding: 12px 14px; }
    .tm-row { display: flex; flex-wrap: wrap; align-items: flex-end; gap: 8px 10px; }
    .tm-arrow { padding-bottom: 8px; color: var(--docs-ft-color-muted); }
    .tm-field { display: flex; flex-direction: column; gap: 3px; flex: 1 1 180px; font-size: 11px; color: var(--docs-ft-color-muted); }
    .tm-input { height: 28px; padding: 0 8px; font: inherit; font-size: var(--font-size); color: var(--global-text-color-1); border: 1px solid var(--input-bd-color); border-radius: var(--docs-br); background: var(--input-bg-color); }
    .tm-muted { color: var(--docs-ft-color-muted); font-size: 12px; margin: 0 0 10px; }
    .tm-result { margin-top: 12px; }
    .tm-result p { margin: 4px 0; }
    .tm-warn, .tm-error { display: flex; gap: 6px; align-items: baseline; }
    .tm-error { color: var(--docs-color-danger); margin: 10px 0 0; }
    .tm-spacer { flex: 1 1 auto; }
    .tm-icon { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; border: none; background: transparent; color: inherit; cursor: pointer; border-radius: var(--docs-br); }
    .p-button.tm-btn { height: 28px; margin: 0; padding: 0 12px; font-size: var(--font-size); }
    .tm-btn i { margin-right: 6px; }
    @media (max-width: 480px) { .tm-arrow { display: none; } .tm-foot .tm-btn { flex: 1 1 auto; } }
  `,
})
export class DocumentTagManagerComponent {
  /** known tag names (suggestions); only the user tags can be renamed */
  readonly tagNames = input<DocTag[]>([]);
  readonly closed = output<void>();
  readonly renamed = output<DocTagRename>();

  readonly #docs = inject(DocumentsService);
  readonly #translate = inject(TranslateService);
  readonly #refresh = viewRefresher();
  #sub: Subscription | undefined;

  readonly from = signal('');
  readonly to = signal('');
  readonly busy = signal(false);
  readonly error = signal<string | null>(null);
  /** count for the names currently typed (cleared when a name changes) */
  readonly checked = signal<DocTagRename | null>(null);

  readonly names = computed(() => this.tagNames().filter((t) => t.editable));
  readonly valid = computed(() => {
    const f = tagName(this.from());
    const t = tagName(this.to());
    return /^[A-Z0-9_]{1,60}$/.test(f) && /^[A-Z0-9_]{1,60}$/.test(t) && f !== t;
  });
  /** the new name is already used: a merge */
  readonly existing = computed(() => this.tagNames().some((t) => t.key === tagName(this.to())));

  constructor() {
    inject(DestroyRef).onDestroy(() => this.#sub?.unsubscribe());
  }

  setFrom(v: string): void {
    this.from.set(v);
    this.checked.set(null);
    this.error.set(null);
  }

  setTo(v: string): void {
    this.to.set(v);
    this.checked.set(null);
    this.error.set(null);
  }

  check(): void {
    if (!this.valid() || this.busy()) return;
    this.#run(false);
  }

  apply(): void {
    const c = this.checked();
    if (!c || c.matched === 0 || this.busy()) return;
    this.#run(true);
  }

  #run(apply: boolean): void {
    this.busy.set(true);
    this.error.set(null);
    this.#sub?.unsubscribe();
    this.#sub = this.#docs.renameTag(tagName(this.from()), tagName(this.to()), apply).subscribe({
      next: (r) => {
        this.busy.set(false);
        if (apply) this.renamed.emit(r);
        else this.checked.set(r);
        this.#refresh();
      },
      error: (e) => {
        this.busy.set(false);
        const msg = e instanceof HttpErrorResponse && e.status === 400 && e.error?.message ? e.error.message : null;
        this.error.set(msg ?? this.#translate.instant(e?.status === 403 ? 'documents.state.forbidden' : 'documents.state.error'));
        this.#refresh();
      },
    });
  }
}
